// =====================================================
// TAB INFORME AGROPECUARIO - VERSIÓN MEJORADA
// src/pages/Agro/Productor/InformeAgropecuario.js
// =====================================================
// CORRECCIONES:
// - Colores de headers corregidos (texto oscuro sobre fondos claros)
// - Tab Tambo agregado
// - Diseño profesional para evaluación crediticia
// - KPIs visuales mejorados
// =====================================================

import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Alert,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
} from "reactstrap"
import classnames from "classnames"
import dayjs from "dayjs"
import { formatearFechaSiembra } from "helpers/formatters"
import * as XLSX from "xlsx"
import {
  SeccionDictamen,
  SeccionComparativo,
  SeccionScoring,
  SeccionHistorial,
  SeccionTenencia,
  SeccionFlujoCaja,
} from "./SubComponentesInforme"
import {
  getProductor,
  getEstablecimientos,
  getSiembrasPorProductor,
  getStockPorProductor,
  getResumenCultivos,
  getResumenGanadero,
  generarPdfInformeAgroPorProductor,
  getParametrosInforme,
  getInformeCompleto,
  // BDSJEA-167
  getTambosPorProductor,
} from "api/agro"
import toastHandler from "toastHandler"
import AnalisisSatelitalIA from "./AnalisisSatelitalIA"
import MetodologiaInforme from "./MetodologiaInforme"
import ComparativoInformeProyeccion from "./ComparativoInformeProyeccion"
import { TEXTO_SIEMBRAS_VIGENTES } from "./SiembrasVigentesNota"

// =====================================================
// ESTILOS - COLORES CORREGIDOS
// =====================================================

const STYLES = {
  // Headers de sección con texto OSCURO sobre fondos claros
  headerResumen: {
    background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
    color: "#1b5e20",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerAgricola: {
    background:
      "linear-gradient(135deg, rgba(var(--bs-warning-rgb), 0.08) 0%, rgba(var(--bs-warning-rgb), 0.16) 100%)",
    color: "var(--bs-warning)",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerGanaderia: {
    background: "linear-gradient(135deg, #fce4ec 0%, #f8bbd9 100%)",
    color: "#880e4f",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerTambo: {
    background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
    color: "#0d47a1",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerCliente: {
    background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
    color: "#4a148c",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerDatos: {
    background: "linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)",
    color: "#006064",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  headerSatelital: {
    background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
    fontSize: "1rem",
  },
  // KPI Cards
  kpiCard: {
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "none",
    transition: "transform 0.2s ease",
    height: "100%",
  },
  kpiValue: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#6c757d",
    marginBottom: "4px",
  },
  // Tabs
  tabActive: {
    backgroundColor: "#fff",
    borderBottom: "3px solid #28a745",
    fontWeight: 600,
  },
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const InformeAgropecuario = ({ idProductor, productor: productorProp }) => {
  const { setError, setSuccess } = toastHandler()

  // Estados principales
  const [loading, setLoading] = useState(true)
  const [generandoPdf, setGenerandoPdf] = useState(false)
  const [activeTab, setActiveTab] = useState("resumen")

  // Datos del productor
  const [productor, setProductor] = useState(productorProp || null)
  const [establecimientos, setEstablecimientos] = useState([])
  const [siembras, setSiembras] = useState([])
  const [stockGanadero, setStockGanadero] = useState([])
  const [datosTambo, setDatosTambo] = useState(null)

  // Datos de Excel (si se carga)
  const [datosExcel, setDatosExcel] = useState(null)
  const [origenDatos, setOrigenDatos] = useState("sistema")

  // Modal de carga Excel
  const [modalExcel, setModalExcel] = useState(false)
  const [archivoExcel, setArchivoExcel] = useState(null)
  const [procesandoExcel, setProcesandoExcel] = useState(false)

  // Comentarios del analista
  const [comentarios, setComentarios] = useState("")

  // Análisis satelital
  const [analisisSatelital, setAnalisisSatelital] = useState(null)

  // Parámetros desde la BD (reemplazan constantes hardcodeadas)
  const [parametrosBD, setParametrosBD] = useState(null)
  const [loadingParams, setLoadingParams] = useState(true)

  // Datos del informe completo (proyección, scoring, comparativo, etc.)
  const [informeCompleto, setInformeCompleto] = useState(null)
  const [loadingInforme, setLoadingInforme] = useState(false)

  // =====================================================
  // CARGA DE DATOS
  // =====================================================

  // Cargar parámetros de la BD una sola vez
  useEffect(() => {
    const cargarParametros = async () => {
      setLoadingParams(true)
      try {
        const params = await getParametrosInforme()
        setParametrosBD(params)
      } catch (e) {
        console.warn(
          "No se pudieron cargar parámetros de BD, usando defaults:",
          e.message,
        )
        setParametrosBD({
          rendimientosPorZona: { default: 30 },
          costosPorCultivo: { default: 400 },
          valoresPorCabeza: { default: 800 },
          valoresTambo: {},
          configProyeccion: {},
        })
      } finally {
        setLoadingParams(false)
      }
    }
    cargarParametros()
  }, [])

  // Cargar informe completo (proyección, scoring, comparativo, historial, tenencia)
  useEffect(() => {
    if (!idProductor) return
    let mounted = true
    const cargarInforme = async () => {
      setLoadingInforme(true)
      try {
        const data = await getInformeCompleto(idProductor)
        if (mounted) {
          setInformeCompleto(data)
        }
      } catch (e) {
        const msg =
          e.response?.data?.mensaje ||
          e.message ||
          "Error al cargar el informe completo"
        if (mounted) {
          setError(msg)
        }
      } finally {
        if (mounted) {
          setLoadingInforme(false)
        }
      }
    }
    cargarInforme()
    return () => {
      mounted = false
    }
  }, [idProductor])

  useEffect(() => {
    if (idProductor) {
      // BDSJEA-541: cargarDatosSistema ya apaga `loading` en su propio finally
      // y muestra sus errores reales. No hay que llamar a setError(null) al
      // terminar: el setError del toastHandler es un store global y con null
      // dejaba el toast «Error inesperado» aunque el componente ya no existiera.
      cargarDatosSistema()
    }
  }, [idProductor])

  // Resolver zona: puede venir como provincia ("Buenos Aires")
  // o como zona_agro ("Sudoeste de Buenos Aires") o null/vacía (sin zona)
  const resolverZona = zonaOProvincia => {
    if (!zonaOProvincia) return null

    // Si es una zona_agro directa (matchea con algún valor_agro), devolverla
    if (parametrosBD?.valoresAgro?.some(va => va.zona === zonaOProvincia)) {
      return zonaOProvincia
    }

    // Si es una provincia, buscar en el mapeo provincia → zona
    if (parametrosBD?.provinciaAZona) {
      const zonaMapeada = parametrosBD.provinciaAZona[zonaOProvincia]
      if (zonaMapeada) return zonaMapeada
    }

    // Búsqueda parcial: "Buenos Aires" matchea con "Sudoeste de Buenos Aires"
    if (parametrosBD?.valoresAgro) {
      const match = parametrosBD.valoresAgro.find(
        va =>
          va.zona?.toLowerCase().includes(zonaOProvincia.toLowerCase()) ||
          zonaOProvincia.toLowerCase().includes(va.zona?.toLowerCase()),
      )
      if (match) return match.zona
    }

    return null
  }

  // Helpers que leen de la BD en vez de constantes hardcodeadas
  // Helper: buscar en valoresAgro por zona+cultivo (detalle completo)
  const getValorAgro = (zona, cultivo) => {
    if (!parametrosBD?.valoresAgro) return null

    // Resolver la zona real
    const zonaResuelta = resolverZona(zona)

    // Buscar por zona resuelta + cultivo
    if (zonaResuelta) {
      const match = parametrosBD.valoresAgro.find(
        va =>
          va.zona === zonaResuelta &&
          (va.cultivo === cultivo || va.codigoCultivo === cultivo),
      )
      if (match) return match
    }

    // BDSJEA-144: acá había un fallback a "la primera fila de cualquier zona",
    // que es el orden de carga de la tabla y no un criterio agronómico. Es el
    // mismo patrón que se corrigió en el backend en BDSJEA-125: tomar el
    // rendimiento de una zona ajena desviaba el ingreso proyectado hasta un
    // 133% (soja Cuyo 18 qq/ha contra 42 de zona núcleo). Sin fila para la
    // zona del establecimiento no hay dato, y eso se muestra como tal.
    return null
  }

  // Helper: rendimiento de la zona del establecimiento para un cultivo.
  // BDSJEA-144: devuelve null cuando no hay dato cargado, en vez del 30 qq/ha
  // genérico que antes se mostraba como si fuera un rendimiento real.
  const getRendimientoZona = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    // BDSJEA-502: sin fila de valor_agro de ESA zona y ese cultivo no hay dato
    // ("s/d"). Se quitó el fallback a rendimientosPorZona (un rendimiento por
    // zona sin cultivo, no un dato del cultivo).
    if (va?.rendimiento) return Number(va.rendimiento)
    return null
  }

  // Helper: precio por zona+cultivo. BDSJEA-144: devuelve null en vez de un
  // precio fijo de 280 USD/tn.
  const getPrecioZona = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    if (va?.precio) return Number(va.precio)
    return null
  }

  // Helper: costo total por zona+cultivo (costos_total de la tabla)
  // BDSJEA-144: el default de 400 USD/ha era una magnitud de COSTO DIRECTO, y
  // acá se usa como costo completo. BDSJEA-76 midió el costo completo real en
  // 905 USD/ha para soja y 976 para maíz: devolver 400 subestimaba el costo a
  // menos de la mitad e inflaba el margen. Sin dato cargado devuelve null y la
  // pantalla muestra s/d.
  const getCostoTotal = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    // BDSJEA-502: costos_total YA incluye costo directo, gastos de
    // comercialización, arrendamientos, administración y seguro (V0128). Sin fila
    // de la zona y el cultivo no hay dato ("s/d"): se quitó el fallback a
    // costosPorCultivo, que mostraba un costo aunque el ingreso diera 0.
    // Un costosTotal = 0 se trata como "sin dato" (s/d), a propósito: un costo de
    // 0 USD/ha no es un costo real cargado, es una fila sin completar.
    if (va?.costosTotal) return Number(va.costosTotal)
    return null
  }

  // BDSJEA-502: costo directo por ha (solo desglose informativo, nunca se suma
  // al costo total).
  const getCostoDirectoHa = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    return va?.costoDirecto ? Number(va.costoDirecto) : 0
  }

  // Helper: gastos de comercialización por zona+cultivo (USD/ha)
  const getGastosComercializacion = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    return va?.gastosComercializacion ? Number(va.gastosComercializacion) : 0
  }

  // Helper: arrendamientos por zona+cultivo (USD/ha)
  const getArrendamientos = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    return va?.arrendamientos ? Number(va.arrendamientos) : 0
  }

  // Helper: admin siembra por zona+cultivo (USD/ha)
  const getAdminSiembra = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    return va?.administracionSiembra ? Number(va.administracionSiembra) : 0
  }

  // Helper: seguro granizo por zona+cultivo (USD/ha)
  const getSeguroGranizo = (zona, cultivo) => {
    const va = getValorAgro(zona, cultivo)
    return va?.seguroGranizo ? Number(va.seguroGranizo) : 0
  }

  // Helper: buscar en valoresGanaderia por tipo de hacienda (detalle completo)
  const getValorGanaderia = tipoHacienda => {
    if (!parametrosBD?.valoresGanaderia) return null
    const nombre = (tipoHacienda || "").toLowerCase().trim()

    // Búsqueda exacta (case insensitive)
    let match = parametrosBD.valoresGanaderia.find(
      vg => vg.tipoHacienda?.toLowerCase() === nombre,
    )
    if (match) return match

    // Búsqueda singular/plural
    // "Novillo" → "Novillos" y viceversa
    const conS = nombre.endsWith("s") ? nombre : nombre + "s"
    const sinS = nombre.endsWith("s") ? nombre.slice(0, -1) : nombre
    match = parametrosBD.valoresGanaderia.find(vg => {
      const t = vg.tipoHacienda?.toLowerCase()
      return t === conS || t === sinS
    })
    if (match) return match

    // Búsqueda parcial
    match = parametrosBD.valoresGanaderia.find(
      vg =>
        vg.tipoHacienda?.toLowerCase().includes(nombre) ||
        nombre.includes(vg.tipoHacienda?.toLowerCase()),
    )
    return match || null
  }

  // Helper: valor por cabeza (peso * precio_kilo)
  const getValorCabeza = tipoHacienda => {
    const vg = getValorGanaderia(tipoHacienda)
    if (vg?.pesoVenta && vg?.precioKilo) {
      return Number(vg.pesoVenta) * Number(vg.precioKilo)
    }
    return parametrosBD?.valoresPorCabeza?.[tipoHacienda] || 800
  }

  // Helper: gastos de venta ganadería (%)
  const getGastosVentaGanaderia = tipoHacienda => {
    const vg = getValorGanaderia(tipoHacienda)
    return vg?.gastosVenta ? Number(vg.gastosVenta) / 100 : 0.08
  }

  // Helper: gastos productivos ganadería (USD/cabeza)
  const getGastosProductivosGanaderia = tipoHacienda => {
    const vg = getValorGanaderia(tipoHacienda)
    return vg?.gastosProductivos ? Number(vg.gastosProductivos) : 0
  }

  // Helper: gastos cría ganadería (USD/cabeza)
  const getGastosCriaGanaderia = tipoHacienda => {
    const vg = getValorGanaderia(tipoHacienda)
    return vg?.gastosCria ? Number(vg.gastosCria) : 0
  }

  // Helper: valor general por concepto normalizado
  /**
   * BDSJEA-167: coincidencia EXACTA del concepto, no `includes`.
   *
   * La versión anterior buscaba por subcadena y devolvía el primer match del
   * array. Con eso, "reposición" encontraba "Precio vaquillona de reposición"
   * —que viene antes por id— en lugar de "% de reposición", y el porcentaje de
   * reposición del tambo quedaba en 1800 en vez de 20. El síntoma no se veía en
   * ningún lado salvo mirando el margen.
   *
   * Si el concepto se renombra en Administración, ahora se cae al default en
   * lugar de tomar el valor de otro parámetro.
   */
  const getValorGeneral = (codigo, defaultVal) => {
    if (!parametrosBD?.valoresGenerales) return defaultVal
    const found = parametrosBD.valoresGenerales.find(vg => vg.codigo === codigo)
    return found ? Number(found.valor) : defaultVal
  }

  // Helper: config proyección
  const getConfigProy = (key, defaultVal) => {
    if (!parametrosBD) return defaultVal
    const val = parametrosBD.configProyeccion?.[key]
    return val ? parseFloat(val) : defaultVal
  }

  // Valores tambo completos desde valor_general
  const VALORES_TAMBO = {
    // BDSJEA-171: por código, no por el texto visible del concepto.
    pctTambero: getValorGeneral("PCT_TAMBERO", 12) / 100,
    precioLitro: getValorGeneral("PRECIO_LITRO_LECHE", 0.45),
    gastoAnualVaca: getValorGeneral("GASTO_ANUAL_VACA", 280),
    pctReposicion: getValorGeneral("PCT_REPOSICION", 20) / 100,
    precioVaquillona: getValorGeneral("PRECIO_VAQUILLONA", 1800),
    costoCompra: getValorGeneral("COSTO_COMPRA", 500),
  }

  const cargarDatosSistema = async () => {
    setLoading(true)
    try {
      const [prodData, estData, siembraData, stockData] = await Promise.all([
        productorProp
          ? Promise.resolve(productorProp)
          : getProductor(idProductor),
        getEstablecimientos(idProductor),
        getSiembrasPorProductor(idProductor),
        getStockPorProductor(idProductor),
      ])

      setProductor(prodData)
      setEstablecimientos(estData || [])
      setSiembras(siembraData || [])
      setStockGanadero(stockData || [])

      // BDSJEA-167: el tambo se lee de agro.tambo.
      //
      // Antes se infería del stock ganadero buscando categorías "Vacas en
      // ordeñe" o especie "lechero". Ninguna de las 35 categorías de
      // dom_categoria_ganadera cumple eso, así que el filtro nunca devolvía
      // nada; y cuando devolvía algo, estimaba la producción con un 22
      // litros/vaca/día escrito acá mismo. Ahora el dato lo carga el usuario en
      // la pestaña Tambo y el cálculo lo hace el backend con los parámetros de
      // Valores Generales.
      try {
        const tambos = await getTambosPorProductor(idProductor)
        if (tambos && tambos.length > 0) {
          const totales = tambos.reduce(
            (acc, t) => ({
              vacasOrdene: acc.vacasOrdene + (t.vacasEnOrdene || 0),
              totalCabezas: acc.totalCabezas + (t.totalCabezas || 0),
              produccionDiaria: acc.produccionDiaria + Number(t.litrosDia || 0),
              produccionAnual: acc.produccionAnual + Number(t.litrosAnio || 0),
              ingresoAnual: acc.ingresoAnual + Number(t.ingresoAnualUsd || 0),
              costoAnual: acc.costoAnual + Number(t.costoAnualUsd || 0),
              margenAnual: acc.margenAnual + Number(t.margenAnualUsd || 0),
            }),
            {
              vacasOrdene: 0,
              totalCabezas: 0,
              produccionDiaria: 0,
              produccionAnual: 0,
              ingresoAnual: 0,
              costoAnual: 0,
              margenAnual: 0,
            },
          )
          setDatosTambo({
            ...totales,
            // BDSJEA-167: el anual sale de x365 y el mensual salía de x30, con
            // lo cual 12 meses no daban el año (792.000 contra 803.000). Se
            // deriva del anual para que los tres números sean consistentes.
            produccionMensual: Math.round(totales.produccionAnual / 12),
            detalle: tambos,
          })
        } else {
          // Sin tambo cargado la sección no se muestra, en lugar de mostrarse
          // vacía o con estimaciones.
          setDatosTambo(null)
        }
      } catch (e) {
        console.warn("No se pudieron cargar los datos de tambo:", e)
        setDatosTambo(null)
      }

      // console.log("✅ Datos cargados del sistema EWAN:", {
      //   productor: prodData?.razonSocial,
      //   establecimientos: estData?.length || 0,
      //   siembras: siembraData?.length || 0,
      //   stockGanadero: stockData?.length || 0
      // })
    } catch (error) {
      console.error("Error cargando datos:", error)
      setError("Error al cargar datos del productor")
    } finally {
      setLoading(false)
    }
  }

  // =====================================================
  // PROCESAMIENTO EXCEL
  // =====================================================

  const procesarExcel = async () => {
    if (!archivoExcel) return

    setProcesandoExcel(true)
    try {
      const buffer = await archivoExcel.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: "array" })

      const datos = {
        caratula: {},
        cultivos: [],
        ganaderia: { cria: {}, ventas: {}, invernada: {} },
        tambo: {},
      }

      // console.log("📊 Hojas encontradas:", workbook.SheetNames)

      // =====================================================
      // Procesar hoja Carátula
      // =====================================================
      if (workbook.SheetNames.includes("Caratula")) {
        const sheet = workbook.Sheets["Caratula"]
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        // Buscar la fila de headers (contiene "Fecha", "CUIT", "Razon Social", etc.)
        let headerRowIdx = -1
        for (let i = 0; i < data.length; i++) {
          const row = data[i]
          if (
            row &&
            row.some(
              cell =>
                String(cell || "")
                  .toLowerCase()
                  .includes("cuit") ||
                String(cell || "")
                  .toLowerCase()
                  .includes("razon social") ||
                String(cell || "")
                  .toLowerCase()
                  .includes("razón social"),
            )
          ) {
            headerRowIdx = i
            break
          }
        }

        if (headerRowIdx >= 0 && data[headerRowIdx + 1]) {
          const headers = data[headerRowIdx]
          const values = data[headerRowIdx + 1]

          headers.forEach((header, idx) => {
            if (header && values[idx] !== undefined) {
              const key = String(header)
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "") // quitar acentos
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, "")
              datos.caratula[key] = values[idx]
            }
          })
        }

        // console.log("📋 Carátula procesada:", datos.caratula)
      }

      // =====================================================
      // Procesar hoja Cultivo
      // =====================================================
      if (workbook.SheetNames.includes("Cultivo")) {
        const sheet = workbook.Sheets["Cultivo"]
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        // Buscar la fila de headers (contiene "Cultivo", "Zona", "Ha")
        let headerRowIdx = -1
        for (let i = 0; i < data.length; i++) {
          const row = data[i]
          if (
            row &&
            row.some(
              cell =>
                String(cell || "").toLowerCase() === "cultivo" ||
                String(cell || "")
                  .toLowerCase()
                  .includes("zona"),
            )
          ) {
            headerRowIdx = i
            break
          }
        }

        if (headerRowIdx >= 0) {
          const headers = data[headerRowIdx].map(h => String(h || "").trim())

          // Encontrar índices de las columnas
          const cultivoIdx = headers.findIndex(
            h => h.toLowerCase() === "cultivo",
          )
          const zonaIdx = headers.findIndex(h =>
            h.toLowerCase().includes("zona"),
          )
          const haIdx = headers.findIndex(
            h =>
              h.toLowerCase().includes("ha") ||
              h.toLowerCase().includes("hectarea"),
          )

          // Procesar filas de datos
          for (let i = headerRowIdx + 1; i < data.length; i++) {
            const row = data[i]
            if (!row) continue

            const cultivo = row[cultivoIdx]
            const zona = row[zonaIdx]
            const hectareas = row[haIdx]

            if (cultivo && hectareas) {
              datos.cultivos.push({
                Cultivo: String(cultivo).trim(),
                Zona: zona ? String(zona).trim() : null,
                "Superficie (ha)": Number(hectareas) || 0,
                Establecimiento: "Excel Import",
              })
            }
          }
        }

        // console.log("🌾 Cultivos procesados:", datos.cultivos)
      }

      // =====================================================
      // Procesar hoja Ganaderia
      // =====================================================
      if (workbook.SheetNames.includes("Ganaderia")) {
        const sheet = workbook.Sheets["Ganaderia"]
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 })

        // Buscar la fila que tiene "Cria", "Ventas", "Invernada"
        let sectionRowIdx = -1
        for (let i = 0; i < data.length; i++) {
          const row = data[i]
          if (
            row &&
            row.some(
              cell =>
                String(cell || "").toLowerCase() === "cria" ||
                String(cell || "").toLowerCase() === "cría",
            )
          ) {
            sectionRowIdx = i
            break
          }
        }

        if (sectionRowIdx >= 0) {
          const sectionRow = data[sectionRowIdx]

          // Encontrar columnas de cada sección
          let criaCol = -1,
            ventasCol = -1,
            invernadaCol = -1
          sectionRow.forEach((cell, idx) => {
            const val = String(cell || "").toLowerCase()
            if (val === "cria" || val === "cría") criaCol = idx
            else if (val === "ventas") ventasCol = idx
            else if (val === "invernada") invernadaCol = idx
          })

          // Procesar filas de datos
          for (let i = sectionRowIdx + 1; i < data.length; i++) {
            const row = data[i]
            if (!row) continue

            // Cría (columna criaCol tiene categoría, criaCol+1 tiene cantidad)
            if (criaCol >= 0 && row[criaCol] && row[criaCol + 1]) {
              const categoria = String(row[criaCol]).trim()
              const cantidad = Number(row[criaCol + 1]) || 0
              if (categoria && cantidad > 0) {
                datos.ganaderia.cria[categoria] = cantidad
              }
            }

            // Ventas (columna ventasCol tiene categoría, ventasCol+1 tiene cantidad)
            if (ventasCol >= 0 && row[ventasCol] && row[ventasCol + 1]) {
              const categoria = String(row[ventasCol]).trim()
              const cantidad = Number(row[ventasCol + 1]) || 0
              if (categoria && cantidad > 0) {
                datos.ganaderia.ventas[categoria] = cantidad
              }
            }

            // Invernada (columna invernadaCol tiene categoría, invernadaCol+1 tiene cantidad)
            if (
              invernadaCol >= 0 &&
              row[invernadaCol] &&
              row[invernadaCol + 1]
            ) {
              const categoria = String(row[invernadaCol]).trim()
              const cantidad = Number(row[invernadaCol + 1]) || 0
              if (categoria && cantidad > 0) {
                datos.ganaderia.invernada[categoria] = cantidad
              }
            }
          }
        }

        // console.log("🐄 Ganadería procesada:", datos.ganaderia)
      }

      // =====================================================
      // Procesar hoja Tambo
      // =====================================================
      if (workbook.SheetNames.includes("Tambo")) {
        const sheet = workbook.Sheets["Tambo"]
        const data = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: null,
        })

        // console.log("🥛 Datos crudos de Tambo:", data)

        // Estructura clave-valor (columna 1 = clave, columna 2 = valor)
        data.forEach((row, rowIdx) => {
          if (!row) return

          // La clave está en columna 1, el valor en columna 2
          const rawKey = row[0]
          const rawValue = row[1]

          // console.log(`🥛 Fila ${rowIdx}: key="${rawKey}", value="${rawValue}", valueType=${typeof rawValue}`)

          // Solo procesar si hay clave y el valor no es null/undefined/vacío
          if (
            rawKey &&
            rawValue !== null &&
            rawValue !== undefined &&
            rawValue !== ""
          ) {
            const key = String(rawKey)
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "") // quitar acentos
              .replace(/\s+/g, "_")
              .replace(/[^a-z0-9_]/g, "")

            let value = rawValue
            // Intentar extraer número si es string con "usd" u otras unidades
            if (typeof value === "string") {
              const numMatch = value.match(/[\d.]+/)
              if (numMatch) {
                value = Number(numMatch[0])
              }
            }

            datos.tambo[key] = value

            // Mapear keys específicos a nombres estandarizados
            const keyLower = String(rawKey).toLowerCase()
            if (keyLower.includes("vacas") && keyLower.includes("orde")) {
              datos.tambo.vacasOrdene = Number(value) || 0
            }
            if (keyLower.includes("litros") && keyLower.includes("dia")) {
              datos.tambo.litrosPorDia = Number(value) || 22
            }
          }
        })

        // Calcular producción si tenemos vacas en ordeñe
        const vacasOrdene =
          datos.tambo.vacasOrdene ||
          datos.tambo.vacas_en_ordene ||
          datos.tambo.vacas_ordene ||
          0
        const litrosPorDia =
          datos.tambo.litrosPorDia ||
          datos.tambo.litros_de_vacas_por_dia ||
          datos.tambo.litros_por_dia ||
          22

        // console.log("🥛 Valores calculados - vacasOrdene:", vacasOrdene, "litrosPorDia:", litrosPorDia)

        if (vacasOrdene > 0) {
          datos.tambo.vacasOrdene = vacasOrdene
          datos.tambo.litrosPorDia = litrosPorDia
          datos.tambo.produccionDiaria = vacasOrdene * litrosPorDia
          datos.tambo.produccionMensual = vacasOrdene * litrosPorDia * 30
        }

        // console.log("🥛 Tambo procesado:", datos.tambo)
      }

      setDatosExcel(datos)
      setOrigenDatos("excel")
      setModalExcel(false)
      setSuccess("Archivo Excel procesado correctamente")

      // console.log("✅ Datos Excel completos:", datos)
    } catch (error) {
      console.error("Error procesando Excel:", error)
      setError("Error al procesar el archivo Excel: " + error.message)
    } finally {
      setProcesandoExcel(false)
    }
  }

  const agruparStockPorActividad = (stock, actividad) => {
    const resultado = {}
    stock.forEach(item => {
      const itemActividad = (
        item.actividad ||
        item.sistemaProductivo ||
        "cria"
      ).toLowerCase()
      if (itemActividad.includes(actividad)) {
        const categoria = item.categoria || item.descripcion || "Otros"
        resultado[categoria] =
          (resultado[categoria] || 0) +
          (item.cantidadCabezas || item.cantidad || 0)
      }
    })
    return resultado
  }

  // =====================================================
  // TRANSFORMACIÓN DE DATOS
  // =====================================================

  const getDatosActuales = useMemo(() => {
    if (origenDatos === "excel" && datosExcel) {
      // console.log("📊 Usando datos de Excel:", datosExcel)

      return {
        cliente: {
          razonSocial:
            datosExcel.caratula?.razon_social ||
            datosExcel.caratula?.razonsocial ||
            datosExcel.caratula?.cliente ||
            "N/D",
          cuit: datosExcel.caratula?.cuit || "N/D",
          localidad: datosExcel.caratula?.localidad || "N/D",
          provincia:
            datosExcel.caratula?.provincia ||
            datosExcel.caratula?.zona ||
            "N/D",
          actividad: datosExcel.caratula?.actividad || "Agropecuario",
          hectareasPropias:
            Number(datosExcel.caratula?.ha_propias) ||
            Number(datosExcel.caratula?.["#_ha_propias"]) ||
            0,
          hectareasArrendadas:
            Number(datosExcel.caratula?.ha_arrendadas) ||
            Number(datosExcel.caratula?.["#_ha_arrendadas"]) ||
            0,
        },
        cultivos: datosExcel.cultivos || [],
        ganaderia: {
          cria: datosExcel.ganaderia?.cria || {},
          ventas: datosExcel.ganaderia?.ventas || {},
          invernada: datosExcel.ganaderia?.invernada || {},
          tambo: {},
        },
        tambo: datosExcel.tambo || {},
      }
    }

    // Datos del sistema EWAN
    return {
      cliente: {
        razonSocial: productor?.razonSocial || productor?.nombre || "N/D",
        cuit: productor?.cuit || "N/D",
        localidad:
          productor?.localidad || establecimientos[0]?.localidad || "N/D",
        provincia:
          productor?.provincia || establecimientos[0]?.provincia || "N/D",
      },
      // BDSJEA-502: solo siembras vigentes (el backend marca `vigente`; sin el
      // campo se asume vigente). La zona sale del establecimiento de la siembra
      // (idEstablecimiento -> nombreZona de getEstablecimientos); un
      // establecimiento que no está en la lista (inactivo) queda sin zona: "s/d".
      cultivos: siembras
        .filter(s => s.vigente !== false)
        .map(s => {
          const est = establecimientos.find(
            e => e.id != null && e.id === s.idEstablecimiento,
          )
          const nombreEst =
            (typeof s.establecimiento === "string"
              ? s.establecimiento
              : s.establecimiento?.nombre) ||
            s.nombreEstablecimiento ||
            est?.nombre ||
            "N/D"
          return {
            Cultivo: s.cultivo || s.tipoCultivo,
            Establecimiento: nombreEst,
            "Superficie (ha)": s.superficieHa || s.superficie || 0,
            Campaña: s.campana || s.campaña || "N/D",
            // BDSJEA-512: real; si no hay, la estimada del plan con (est.); si no, N/D
            "Fecha Siembra": formatearFechaSiembra(
              s.fechaSiembra,
              s.fechaSiembraEstimada,
              "N/D"
            ),
            Estado: s.estadoFenologico || s.estado || "N/D",
            Zona: est?.nombreZona || s.zona || null,
            // Establecimiento fuera del listado (inactivo): sin zona => "s/d"
            SinZonaInactivo: !est,
          }
        }),
      ganaderia: {
        cria: agruparStockPorActividad(stockGanadero, "cria"),
        invernada: agruparStockPorActividad(stockGanadero, "invernada"),
        tambo: agruparStockPorActividad(stockGanadero, "tambo"),
      },
      tambo: datosTambo || {},
    }
  }, [
    origenDatos,
    datosExcel,
    productor,
    siembras,
    stockGanadero,
    establecimientos,
    datosTambo,
  ])

  // =====================================================
  // CÁLCULO DE MÉTRICAS
  // =====================================================

  // BDSJEA-502: cálculo único por cultivo, usado por las métricas (tarjetas) y
  // por la tabla «Actividades de Cultivo». Rendimiento y precio: declarados >
  // valor_agro de la zona + cultivo. Sin dato => ingreso null ("s/d"); sin costo
  // en valor_agro => costo null ("s/d"). El costo es superficie x costos_total
  // (que ya incluye las demás partidas); el desglose es solo informativo.
  const calcularCultivo = c => {
    const superficie =
      Number(c["Superficie (ha)"]) ||
      Number(c.Superficie) ||
      Number(c.hectareas) ||
      0
    const zona = c.Zona || null
    const cultivo = c.Cultivo || ""
    const rendimiento =
      Number(c.Rendimiento) || getRendimientoZona(zona, cultivo) || 0
    const precioTn =
      Number(c["Precio (USD/tn)"]) ||
      Number(c.Precio) ||
      getPrecioZona(zona, cultivo) ||
      0
    const costoHa = getCostoTotal(zona, cultivo)
    const produccionTn = (superficie * rendimiento) / 10 // qq/ha → tn
    const ingreso = rendimiento && precioTn ? produccionTn * precioTn : null
    const costo = costoHa ? superficie * costoHa : null
    return {
      superficie,
      zona,
      cultivo,
      rendimiento,
      precioTn,
      produccionTn,
      ingreso,
      costo,
      margen: ingreso != null && costo != null ? ingreso - costo : null,
      costoDirecto: superficie * getCostoDirectoHa(zona, cultivo),
      gastosComerc: superficie * getGastosComercializacion(zona, cultivo),
      arrend: superficie * getArrendamientos(zona, cultivo),
      admin: superficie * getAdminSiembra(zona, cultivo),
      seguro: superficie * getSeguroGranizo(zona, cultivo),
    }
  }

  // BDSJEA-502: campañas de las siembras que entran en el cálculo (rótulo).
  const campanasIncluidas = [
    ...new Set(
      getDatosActuales.cultivos
        .map(c => c.Campaña)
        .filter(x => x && x !== "N/D"),
    ),
  ].sort()

  const metricas = useMemo(() => {
    const datos = getDatosActuales

    // =============================================
    // AGRÍCOLA - usando valor_agro completo
    // =============================================
    let totalSuperficie = 0
    let ingresosBrutoTotal = 0
    let costosTotales = 0
    let gastosComercTotal = 0
    let arrendamientosTotal = 0
    let adminSiembraTotal = 0
    let seguroGranizoTotal = 0
    const detalleCultivos = []

    datos.cultivos.forEach(c => {
      // BDSJEA-502: mismo cálculo que la tabla «Actividades de Cultivo»
      // (calcularCultivo): el total de las tarjetas es la suma de sus filas.
      const k = calcularCultivo(c)
      const { superficie, zona, cultivo, rendimiento, precioTn, produccionTn } =
        k
      const ingresoBruto = k.ingreso ?? 0
      // costos_total ya incluye las 5 partidas: NO se suman otra vez.
      const costoTotalCultivo = k.costo ?? 0
      const costoDirecto = k.costoDirecto
      const gastosComercCultivo = k.gastosComerc
      const arrendCultivo = k.arrend
      const adminCultivo = k.admin
      const seguroCultivo = k.seguro
      const margenCultivo = ingresoBruto - costoTotalCultivo

      totalSuperficie += superficie
      ingresosBrutoTotal += ingresoBruto
      costosTotales += costoTotalCultivo
      gastosComercTotal += gastosComercCultivo
      arrendamientosTotal += arrendCultivo
      adminSiembraTotal += adminCultivo
      seguroGranizoTotal += seguroCultivo

      detalleCultivos.push({
        cultivo,
        zona,
        superficie,
        rendimiento,
        precioTn,
        produccionTn,
        ingresoBruto,
        costoDirecto,
        gastosComercCultivo,
        arrendCultivo,
        adminCultivo,
        seguroCultivo,
        costoTotalCultivo,
        margenCultivo,
        roi:
          costoTotalCultivo > 0 ? (margenCultivo / costoTotalCultivo) * 100 : 0,
      })
    })

    // =============================================
    // GANADERÍA - usando valor_ganaderia completo
    // =============================================
    let totalCabezas = 0
    let valorStock = 0
    let ingresoGanadero = 0
    let costosGanaderia = 0

    // Cría
    Object.entries(datos.ganaderia.cria || {}).forEach(([cat, cant]) => {
      totalCabezas += cant
      valorStock += cant * getValorCabeza(cat)
      // Gastos productivos + cría
      costosGanaderia +=
        cant *
        (getGastosProductivosGanaderia(cat) + getGastosCriaGanaderia(cat))
    })

    // Ventas (del Excel) - estas son ventas específicas
    Object.entries(datos.ganaderia.ventas || {}).forEach(([cat, cant]) => {
      const valorBruto = cant * getValorCabeza(cat)
      const gastosVenta = valorBruto * getGastosVentaGanaderia(cat)
      ingresoGanadero += valorBruto - gastosVenta
    })

    // Invernada
    Object.entries(datos.ganaderia.invernada || {}).forEach(([cat, cant]) => {
      totalCabezas += cant
      const valorCabeza = getValorCabeza(cat)
      valorStock += cant * valorCabeza
      costosGanaderia += cant * getGastosProductivosGanaderia(cat)

      // Si no hay ventas específicas, estimar 40% de venta anual
      if (Object.keys(datos.ganaderia.ventas || {}).length === 0) {
        const ingresoBruto = cant * valorCabeza * 0.4
        const gastosVenta = ingresoBruto * getGastosVentaGanaderia(cat)
        ingresoGanadero += ingresoBruto - gastosVenta
      }
    })

    const margenGanaderia = ingresoGanadero - costosGanaderia

    // =============================================
    // TAMBO
    // =============================================
    //
    // BDSJEA-167: los valores los calcula TamboService y llegan en datosTambo.
    // Acá había una segunda fórmula que recalculaba todo con VALORES_TAMBO, y
    // no coincidía con la del backend: usaba solo las vacas en ordeñe como base
    // del gasto anual y de la reposición, mientras que el servicio usa todo el
    // rodeo. Sobre AGROVALU los dos márgenes daban 243.988 y 244.388 — una
    // diferencia de 400 USD que era pura casualidad, porque los desvíos casi se
    // cancelaban. Con otra composición de rodeo la brecha habría sido grande.
    //
    // Una sola fórmula, la del backend, que además es la que entra en la
    // proyección crediticia y en el dictamen.
    const tamboData = datosTambo || {}
    const ingresoTambo = Number(tamboData.ingresoAnual || 0)
    const costosTambo = Number(tamboData.costoAnual || 0)
    const margenTambo = Number(tamboData.margenAnual || 0)

    const detalleTambo =
      tamboData.vacasOrdene > 0
        ? {
            vacasOrdene: tamboData.vacasOrdene,
            totalCabezas: tamboData.totalCabezas,
            produccionDiariaLts: tamboData.produccionDiaria,
            produccionAnualLts: tamboData.produccionAnual,
            ingresoTambo,
            costosTamboTotal: costosTambo,
            margenTambo,
            // El desglose por concepto lo tiene el detalle por establecimiento
            // que devuelve el backend; acá se muestran los totales.
            detallePorEstablecimiento: tamboData.detalle || [],
          }
        : null

    // =============================================
    // TOTALES
    // =============================================
    const margenBrutoAgricola = ingresosBrutoTotal - costosTotales
    const ingresoTotal = ingresosBrutoTotal + ingresoGanadero + ingresoTambo
    const costoTotal = costosTotales + costosGanaderia + costosTambo
    const margenNetoTotal = ingresoTotal - costoTotal

    const factorCapacidadPago = getConfigProy(
      "CAPACIDAD_PAGO.FACTOR_CAPACIDAD_PAGO",
      0.7,
    )

    return {
      // Agrícola
      totalSuperficie,
      ingresosBrutoTotal,
      costosTotales,
      margenBrutoTotal: margenBrutoAgricola,
      rentabilidadAgricola:
        ingresosBrutoTotal > 0
          ? (margenBrutoAgricola / ingresosBrutoTotal) * 100
          : 0,
      // Desglose costos agrícola
      gastosComercTotal,
      arrendamientosTotal,
      adminSiembraTotal,
      seguroGranizoTotal,
      detalleCultivos,

      // Ganadería
      totalCabezas,
      valorStock,
      ingresoGanadero,
      costosGanaderia,
      margenGanaderia,

      // Tambo
      ingresoTambo,
      costosTambo,
      margenTambo,
      detalleTambo,

      // Totales
      ingresoTotal,
      costoTotal,
      margenNetoTotal,
      rentabilidadGeneral:
        ingresoTotal > 0 ? (margenNetoTotal / ingresoTotal) * 100 : 0,

      // Indicadores crediticios
      capacidadPago: margenNetoTotal * factorCapacidadPago,
      diversificacion:
        (datos.cultivos.length > 0 ? 1 : 0) +
        (totalCabezas > 0 ? 1 : 0) +
        (ingresoTambo > 0 ? 1 : 0),
    }
  }, [getDatosActuales, parametrosBD])

  // =====================================================
  // FORMATEO
  // =====================================================

  /**
   * BDSJEA-167: los decimales se muestran solo cuando el monto los necesita.
   *
   * Antes forzaba 0 decimales siempre, lo que está bien para montos de seis
   * cifras pero convertía el precio del litro de leche —0,45— en "US$ 0". Era
   * el único valor sub-unitario de la pantalla, así que el resto de los
   * parámetros se veían bien y este parecía un dato no cargado.
   */
  const formatMoney = (value, decimales) => {
    if (!value && value !== 0) return "-"
    const num = Number(value)
    const dec =
      decimales != null
        ? decimales
        : Math.abs(num) > 0 && Math.abs(num) < 10
          ? 2
          : 0
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(num)
  }

  const formatPercent = value => {
    if (!value && value !== 0) return "-"
    return `${value.toFixed(1)}%`
  }

  const formatNumber = value => {
    if (!value && value !== 0) return "-"
    return new Intl.NumberFormat("es-AR").format(value)
  }

  // Determina el tipo de actividad principal
  const determinarTipoActividad = () => {
    const tieneAgricola = getDatosActuales.cultivos.length > 0
    const tieneGanaderia = metricas.totalCabezas > 0
    const tieneTambo = metricas.ingresoTambo > 0

    if (tieneAgricola && tieneGanaderia && tieneTambo)
      return "Mixto (Agro-Ganadero-Tambo)"
    if (tieneAgricola && tieneGanaderia) return "Mixto (Agro-Ganadero)"
    if (tieneAgricola && tieneTambo) return "Mixto (Agro-Tambo)"
    if (tieneGanaderia && tieneTambo) return "Mixto (Ganadero-Tambo)"
    if (tieneAgricola) return "Agrícola"
    if (tieneGanaderia) return "Ganadero"
    if (tieneTambo) return "Tambo"
    return "Sin actividad definida"
  }

  // Obtiene la campaña actual basándose en la fecha
  const obtenerCampanaActual = () => {
    const hoy = dayjs()
    const mes = hoy.month() + 1 // dayjs usa 0-indexed
    const anio = hoy.year()

    // Campaña agrícola: julio a junio del año siguiente
    if (mes >= 7) {
      return `${anio}/${anio + 1}`
    } else {
      return `${anio - 1}/${anio}`
    }
  }

  // =====================================================
  // GENERACIÓN PDF
  // =====================================================

  const handleGenerarPdf = async () => {
    setGenerandoPdf(true)
    try {
      // BDSJEA-172: el informe lo arma el backend a partir del id.
      //
      // Acá se construía a mano un payload de más de cien líneas que el PDF
      // usaba como única fuente. Varios campos no se llenaban: la superficie
      // se acumulaba solo dentro del loop de siembras, los costos de tambo no
      // se enviaban y `rentabilidadGeneral` no se calculaba en ningún lado. El
      // documento terminaba diciendo 0 hectáreas y 0% de rentabilidad sobre un
      // productor con 790 ha y 73%, y contradiciéndose consigo mismo.
      const response = await generarPdfInformeAgroPorProductor(idProductor)

      // Descargar el PDF
      const blob = new Blob([response], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `Informe_Agropecuario_${productor?.razonSocial || idProductor}_${dayjs().format("YYYYMMDD")}.pdf`
      link.click()
      window.URL.revokeObjectURL(url)

      setSuccess("PDF generado correctamente")
    } catch (error) {
      console.error("Error generando PDF:", error)
      setError("Error al generar el PDF")
    } finally {
      setGenerandoPdf(false)
    }
  }

  const volverADatosSistema = () => {
    setDatosExcel(null)
    setOrigenDatos("sistema")
  }

  // Detectar si hay datos de tambo (del sistema o del Excel)
  // Simplificado para evitar problemas de dependencias entre useMemo
  const hayDatosTambo = useMemo(() => {
    // 1. Verificar datos del sistema EWAN (estado datosTambo)
    if (
      datosTambo &&
      (datosTambo.vacasOrdene > 0 || datosTambo.produccionMensual > 0)
    ) {
      // console.log("🥛 hayDatosTambo: SÍ (desde sistema EWAN)")
      return true
    }

    // 2. Verificar datos del Excel directamente
    if (origenDatos === "excel" && datosExcel?.tambo) {
      const tamboExcel = datosExcel.tambo
      // console.log("tamboExcel ---> ", tamboExcel)
      const tieneData = Object.keys(tamboExcel).length > 0
      // console.log("tieneData ---> ", tieneData)
      const tieneVacas =
        tamboExcel.vacasOrdene > 0 ||
        tamboExcel.vacas_en_ordene > 0 ||
        Object.keys(tamboExcel).some(k => k.includes("vacas"))
      // console.log("tieneVacas ---> ", tieneVacas)
      const tieneProduccion =
        tamboExcel.produccionMensual > 0 ||
        tamboExcel.produccionDiaria > 0 ||
        tamboExcel.litros_de_vacas_por_dia > 0 ||
        tamboExcel.litrosPorDia > 0
      // console.log("tieneProduccion ---> ", tieneProduccion)

      // console.log("🥛 hayDatosTambo verificando Excel:", {
      //   tamboExcel,
      //   tieneData,
      //   tieneVacas,
      //   tieneProduccion
      // })

      if (tieneData || tieneVacas || tieneProduccion) {
        return true
      }
    }

    // 3. Verificar ganadería tambo del Excel
    if (origenDatos === "excel" && datosExcel?.ganaderia?.tambo) {
      if (Object.keys(datosExcel.ganaderia.tambo).length > 0) {
        // console.log("🥛 hayDatosTambo: SÍ (desde ganaderia.tambo del Excel)")
        return true
      }
    }

    // console.log("🥛 hayDatosTambo: NO")
    return false
  }, [datosTambo, origenDatos, datosExcel])

  // =====================================================
  // RENDER - LOADING
  // =====================================================

  // if (loading) {
  if (loading || loadingParams) {
    return (
      <div className="text-center py-5">
        <Spinner color="success" />
        <p className="mt-3 text-muted">Cargando datos del informe...</p>
      </div>
    )
  }

  const datosActuales = getDatosActuales

  // =====================================================
  // RENDER - PRINCIPAL
  // =====================================================

  return (
    <div className="informe-agropecuario">
      {/* HEADER */}
      <Card className="mb-4 border-0 shadow-sm">
        <CardBody>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                  <i
                    className="mdi mdi-file-document-outline text-success"
                    style={{ fontSize: "1.5rem" }}
                  />
                </div>
                <div>
                  <h4 className="mb-1">Informe Agropecuario Integral</h4>
                  {/* El badge se conserva: si en algun momento vuelve a
                      habilitarse una carga externa, el informe tiene que seguir
                      declarando de donde salieron los datos. Mientras no haya
                      disparador, muestra siempre Sistema EWAN. */}
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Origen:</span>
                    <Badge
                      color={origenDatos === "sistema" ? "success" : "info"}
                      className="px-3"
                    >
                      {origenDatos === "sistema"
                        ? "Sistema EWAN"
                        : "Excel Importado"}
                    </Badge>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              {/* El boton "Cargar Excel" se saco de la cabecera del ITA.
                  Confundia: en una pantalla que consolida datos del sistema,
                  ofrecer una carga externa sugiere que hace falta subir algo
                  para que el informe se genere, cuando no es asi. La
                  importacion sigue existiendo en el codigo (procesarExcel y su
                  modal) por si se decide reubicarla; solo dejo de tener
                  disparador desde aca. */}
              <Button
                color="success"
                size="sm"
                onClick={handleGenerarPdf}
                disabled={generandoPdf}
              >
                {generandoPdf ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="mdi mdi-file-pdf-box me-1" />
                )}
                Generar PDF
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* OBSERVACIONES */}
      {/* <Card className="mb-4 border-0 shadow-sm">
        <CardBody className="py-3">
          <Input
            type="textarea"
            rows={2}
            placeholder="Agregar observaciones del analista..."
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
            className="border-0 bg-light"
            style={{ resize: "none" }}
          />
        </CardBody>
      </Card> */}

      {/* TABS DE NAVEGACIÓN */}
      <style>
        {`
            .nav-tabs-custom .nav-link.active {
              color: #495057 !important;
              background-color: #fff !important;
              font-weight: 600 !important;
              border-color: #dee2e6 #dee2e6 #fff !important;
            }
            .nav-tabs-custom .nav-link {
              color: #6c757d !important;
              cursor: pointer;
            }
            .nav-tabs-custom .nav-link:hover:not(.active) {
              color: #495057 !important;
              border-color: #e9ecef #e9ecef #dee2e6 !important;
            }
          `}
      </style>
      <Nav tabs className="nav-tabs-custom mb-4 border-bottom-0">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "resumen" })}
            onClick={() => setActiveTab("resumen")}
            style={
              activeTab === "resumen" ? STYLES.tabActive : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-chart-line me-2" />
            Resumen
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "agricola" })}
            onClick={() => setActiveTab("agricola")}
            style={
              activeTab === "agricola"
                ? STYLES.tabActive
                : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-sprout me-2" />
            Agrícola
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "ganaderia" })}
            onClick={() => setActiveTab("ganaderia")}
            style={
              activeTab === "ganaderia"
                ? STYLES.tabActive
                : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-cow me-2" />
            Ganadería
          </NavLink>
        </NavItem>
        {hayDatosTambo && (
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "tambo" })}
              onClick={() => setActiveTab("tambo")}
              style={
                activeTab === "tambo" ? STYLES.tabActive : { cursor: "pointer" }
              }
            >
              <i className="mdi mdi-water me-2" />
              Tambo
            </NavLink>
          </NavItem>
        )}
        {/* NUEVA TAB: Análisis Satelital */}
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "satelital" })}
            onClick={() => setActiveTab("satelital")}
            style={
              activeTab === "satelital"
                ? STYLES.tabActive
                : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-satellite-variant me-2" />
            Análisis Satelital
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "proyeccion" })}
            onClick={() => setActiveTab("proyeccion")}
            style={
              activeTab === "proyeccion"
                ? STYLES.tabActive
                : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-trending-up me-2" />
            Proyección
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "scoring" })}
            onClick={() => setActiveTab("scoring")}
            style={
              activeTab === "scoring" ? STYLES.tabActive : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-shield-check me-2" />
            Scoring
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "tenencia" })}
            onClick={() => setActiveTab("tenencia")}
            style={
              activeTab === "tenencia"
                ? STYLES.tabActive
                : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-terrain me-2" />
            Tenencia
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "datos" })}
            onClick={() => setActiveTab("datos")}
            style={
              activeTab === "datos" ? STYLES.tabActive : { cursor: "pointer" }
            }
          >
            <i className="mdi mdi-database me-2" />
            Datos
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        {/* ============================================= */}
        {/* TAB RESUMEN */}
        {/* ============================================= */}
        <TabPane tabId="resumen">
          {/* Metodología */}
          <MetodologiaInforme />
          {/* KPIs Principales */}
          <Row className="mb-4 g-3">
            <Col lg={3} md={6}>
              <Card
                style={STYLES.kpiCard}
                className="border-start border-success border-4"
              >
                <CardBody className="text-center py-4">
                  <p style={STYLES.kpiLabel}>Ingreso Total Estimado</p>
                  <p className="text-success mb-0" style={STYLES.kpiValue}>
                    {formatMoney(metricas.ingresoTotal)}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card
                style={STYLES.kpiCard}
                className="border-start border-primary border-4"
              >
                <CardBody className="text-center py-4">
                  <p style={STYLES.kpiLabel}>Margen Neto</p>
                  <p
                    className={`mb-0 ${metricas.margenNetoTotal >= 0 ? "text-primary" : "text-danger"}`}
                    style={STYLES.kpiValue}
                  >
                    {formatMoney(metricas.margenNetoTotal)}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card
                style={STYLES.kpiCard}
                className="border-start border-info border-4"
              >
                <CardBody className="text-center py-4">
                  <p style={STYLES.kpiLabel}>Capacidad de Pago</p>
                  <p className="text-info mb-0" style={STYLES.kpiValue}>
                    {formatMoney(metricas.capacidadPago)}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col lg={3} md={6}>
              <Card
                style={STYLES.kpiCard}
                className="border-start border-warning border-4"
              >
                <CardBody className="text-center py-4">
                  <p style={STYLES.kpiLabel}>Rentabilidad</p>
                  <p className="text-warning mb-0" style={STYLES.kpiValue}>
                    {formatPercent(metricas.rentabilidadGeneral)}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Dictamen rápido (si hay proyección) */}
          {informeCompleto?.proyeccion && (
            <>
              <SeccionDictamen proyeccion={informeCompleto.proyeccion} />
              <ComparativoInformeProyeccion
                metricas={metricas}
                informeCompleto={informeCompleto}
              />
            </>
          )}

          {/* Resumen por Actividad */}
          <Row className="g-3">
            {/* Agrícola */}
            <Col lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <div style={STYLES.headerAgricola}>
                  <i className="mdi mdi-sprout me-2" />
                  Actividad Agrícola
                </div>
                <CardBody className="text-muted">
                  <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span className="text-muted">Superficie Total</span>
                    <strong>{formatNumber(metricas.totalSuperficie)} ha</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span className="text-muted">Ingresos Brutos</span>
                    <strong className="text-success">
                      {formatMoney(metricas.ingresosBrutoTotal)}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span className="text-muted">Costos Totales</span>
                    <strong className="text-danger">
                      {formatMoney(metricas.costosTotales)}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Margen Bruto</span>
                    <strong
                      className={
                        metricas.margenBrutoTotal >= 0
                          ? "text-success"
                          : "text-danger"
                      }
                    >
                      {formatMoney(metricas.margenBrutoTotal)}
                    </strong>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Ganadería */}
            <Col lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <div style={STYLES.headerGanaderia}>
                  <i className="mdi mdi-cow me-2" />
                  Actividad Ganadera
                </div>
                <CardBody className="text-muted">
                  <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span className="text-muted">Total Cabezas</span>
                    <strong>{formatNumber(metricas.totalCabezas)}</strong>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                    <span className="text-muted">Valor del Stock</span>
                    <strong className="text-primary">
                      {formatMoney(metricas.valorStock)}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Ingreso Estimado</span>
                    <strong className="text-success">
                      {formatMoney(metricas.ingresoGanadero)}
                    </strong>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Tambo / Diversificación */}
            <Col lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <div style={STYLES.headerTambo}>
                  <i className="mdi mdi-water me-2" />
                  {hayDatosTambo ? "Actividad Tambo" : "Diversificación"}
                </div>
                <CardBody>
                  {hayDatosTambo ? (
                    <>
                      <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                        <span className="text-muted">Producción Mensual</span>
                        <strong>
                          {formatNumber(datosTambo?.produccionMensual || 0)} lts
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                        <span className="text-muted">Ingreso Anual</span>
                        <strong className="text-success">
                          {formatMoney(metricas.ingresoTambo)}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Margen</span>
                        <strong
                          className={
                            metricas.margenTambo >= 0
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {formatMoney(metricas.margenTambo)}
                        </strong>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-center py-3">
                        <div className="mb-3">
                          <Badge
                            color={
                              metricas.diversificacion >= 2
                                ? "success"
                                : "warning"
                            }
                            className="px-3 py-2"
                            style={{ fontSize: "1.5rem" }}
                          >
                            {metricas.diversificacion}/3
                          </Badge>
                        </div>
                        <p className="text-muted mb-2">
                          Actividades Productivas
                        </p>
                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                          {datosActuales.cultivos.length > 0 && (
                            <Badge color="success" pill>
                              <i className="mdi mdi-sprout me-1" />
                              Agrícola
                            </Badge>
                          )}
                          {metricas.totalCabezas > 0 && (
                            <Badge color="danger" pill>
                              <i className="mdi mdi-cow me-1" />
                              Ganadería
                            </Badge>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Indicador de Riesgo */}
          <Card className="mt-4 border-0 shadow-sm text-muted">
            <CardBody>
              <h6 className="text-muted mb-3">
                <i className="mdi mdi-shield-check me-2" />
                Evaluación de Riesgo Crediticio
              </h6>
              <Row>
                <Col md={4}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Diversificación</small>
                      <small>{metricas.diversificacion}/3</small>
                    </div>
                    <Progress
                      value={metricas.diversificacion * 33.33}
                      color={
                        metricas.diversificacion >= 2 ? "success" : "warning"
                      }
                      style={{ height: "8px" }}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Rentabilidad</small>
                      <small>
                        {formatPercent(metricas.rentabilidadGeneral)}
                      </small>
                    </div>
                    <Progress
                      value={Math.min(metricas.rentabilidadGeneral, 100)}
                      color={
                        metricas.rentabilidadGeneral >= 15
                          ? "success"
                          : metricas.rentabilidadGeneral >= 5
                            ? "warning"
                            : "danger"
                      }
                      style={{ height: "8px" }}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Cobertura de Pago</small>
                      <small>{metricas.capacidadPago > 0 ? "✓" : "!"}</small>
                    </div>
                    <Progress
                      value={metricas.capacidadPago > 0 ? 100 : 0}
                      color={metricas.capacidadPago > 0 ? "success" : "danger"}
                      style={{ height: "8px" }}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </TabPane>

        {/* ============================================= */}
        {/* TAB AGRÍCOLA */}
        {/* ============================================= */}
        <TabPane tabId="agricola">
          {datosActuales.cultivos.length === 0 ? (
            <Alert color="info" timeout={150}>
              <i className="mdi mdi-information me-2" />
              No hay datos de actividad agrícola. Puede cargar siembras desde el
              tab Actividad Agrícola o importar un Excel.
            </Alert>
          ) : (
            <>
              <Card className="border-0 shadow-sm mb-4">
                <div style={STYLES.headerAgricola}>
                  <i className="mdi mdi-sprout me-2" />
                  Actividades de Cultivo
                  <Badge color="warning" className="ms-2">
                    {datosActuales.cultivos.length}
                  </Badge>
                  {campanasIncluidas.length > 0 && (
                    <small
                      className="ms-2"
                      data-testid="rotulo-campanas"
                      title={TEXTO_SIEMBRAS_VIGENTES}
                    >
                      Siembras vigentes · campaña {campanasIncluidas.join(", ")}
                    </small>
                  )}
                </div>
                <CardBody className="p-0">
                  <Table responsive hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Cultivo</th>
                        <th>Establecimiento</th>
                        <th className="text-end">Superficie</th>
                        <th>Campaña</th>
                        <th>Estado</th>
                        <th className="text-end">Ingreso Est.</th>
                        <th className="text-end">Costo Est.</th>
                        <th className="text-end">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datosActuales.cultivos.map((cultivo, idx) => {
                        // BDSJEA-502: mismo cálculo que las tarjetas
                        // (calcularCultivo); costo = superficie x costos_total
                        // sin sumar las demás partidas.
                        const { superficie, ingreso, costo, margen } =
                          calcularCultivo(cultivo)

                        return (
                          <tr key={idx}>
                            <td>
                              <strong>{cultivo.Cultivo}</strong>
                            </td>
                            <td>{cultivo.Establecimiento}</td>
                            <td className="text-end">
                              {formatNumber(superficie)} ha
                            </td>
                            <td>{cultivo.Campaña || cultivo.Campana}</td>
                            <td>
                              <Badge
                                color={
                                  cultivo.Estado === "Cosechado"
                                    ? "success"
                                    : "info"
                                }
                                pill
                              >
                                {cultivo.Estado}
                              </Badge>
                            </td>
                            <td className="text-end text-success">
                              {ingreso != null ? (
                                formatMoney(ingreso)
                              ) : (
                                <span
                                  className="text-muted"
                                  title={
                                    cultivo.SinZonaInactivo
                                      ? "Establecimiento inactivo: sin zona"
                                      : "Sin rendimiento o precio cargado para la zona y el cultivo"
                                  }
                                >
                                  s/d
                                </span>
                              )}
                            </td>
                            <td className="text-end text-danger">
                              {costo != null ? (
                                formatMoney(costo)
                              ) : (
                                <span
                                  className="text-muted"
                                  title={
                                    cultivo.SinZonaInactivo
                                      ? "Establecimiento inactivo: sin zona"
                                      : "Sin costo cargado para la zona y el cultivo"
                                  }
                                >
                                  s/d
                                </span>
                              )}
                            </td>
                            <td
                              className={`text-end ${margen != null && margen >= 0 ? "text-success" : "text-danger"}`}
                            >
                              <strong>
                                {margen != null ? (
                                  formatMoney(margen)
                                ) : (
                                  <span className="text-muted">s/d</span>
                                )}
                              </strong>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="table-light">
                      <tr className="fw-bold">
                        <td colSpan={2}>TOTALES</td>
                        <td className="text-end">
                          {formatNumber(metricas.totalSuperficie)} ha
                        </td>
                        <td colSpan={2}></td>
                        <td className="text-end text-success">
                          {formatMoney(metricas.ingresosBrutoTotal)}
                        </td>
                        <td className="text-end text-danger">
                          {formatMoney(metricas.costosTotales)}
                        </td>
                        <td
                          className={`text-end ${metricas.margenBrutoTotal >= 0 ? "text-success" : "text-danger"}`}
                        >
                          {formatMoney(metricas.margenBrutoTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </CardBody>
              </Card>

              {/* Resumen Agrícola */}
              <Row className="g-3">
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm">
                    <CardBody>
                      <i
                        className="mdi mdi-land-plots text-warning"
                        style={{ fontSize: "2rem" }}
                      />
                      <h3 className="mt-2 mb-0">
                        {formatNumber(metricas.totalSuperficie)}
                      </h3>
                      <small className="text-muted">Hectáreas Sembradas</small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm">
                    <CardBody>
                      <i
                        className="mdi mdi-currency-usd text-success"
                        style={{ fontSize: "2rem" }}
                      />
                      <h3 className="mt-2 mb-0 text-success">
                        {formatMoney(metricas.ingresosBrutoTotal)}
                      </h3>
                      <small className="text-muted">
                        Ingreso Bruto Estimado
                      </small>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0 shadow-sm">
                    <CardBody>
                      <i
                        className="mdi mdi-percent text-info"
                        style={{ fontSize: "2rem" }}
                      />
                      <h3 className="mt-2 mb-0">
                        {formatPercent(metricas.rentabilidadAgricola)}
                      </h3>
                      <small className="text-muted">
                        Rentabilidad Agrícola
                      </small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB GANADERÍA */}
        {/* ============================================= */}
        <TabPane tabId="ganaderia">
          {metricas.totalCabezas === 0 ? (
            <Alert color="info" timeout={150}>
              <i className="mdi mdi-information me-2" />
              No hay datos de actividad ganadera. Puede cargar stock desde el
              tab Actividad Ganadera o importar un Excel.
            </Alert>
          ) : (
            <Row className="g-4">
              {/* Stock por Categoría */}
              <Col lg={8}>
                <Card className="border-0 shadow-sm text-muted">
                  <div style={STYLES.headerGanaderia}>
                    <i className="mdi mdi-cow me-2" />
                    Stock por Categoría
                  </div>
                  <CardBody className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Categoría</th>
                          <th className="text-end">Cabezas</th>
                          <th className="text-end">Valor Est.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Stock Cría */}
                        {Object.entries(datosActuales.ganaderia.cria || {}).map(
                          ([cat, cant]) => (
                            <tr key={`cria-${cat}`}>
                              <td>
                                <Badge color="light" className="text-dark me-2">
                                  Cría
                                </Badge>
                                {cat}
                              </td>
                              <td className="text-end">{formatNumber(cant)}</td>
                              <td className="text-end text-success">
                                {formatMoney(cant * getValorCabeza(cat))}
                              </td>
                            </tr>
                          ),
                        )}

                        {/* Stock Invernada */}
                        {Object.entries(
                          datosActuales.ganaderia.invernada || {},
                        ).map(([cat, cant]) => (
                          <tr key={`inv-${cat}`}>
                            <td>
                              <Badge color="light" className="text-dark me-2">
                                Invernada
                              </Badge>
                              {cat}
                            </td>
                            <td className="text-end">{formatNumber(cant)}</td>
                            <td className="text-end text-success">
                              {formatMoney(cant * getValorCabeza(cat))}
                            </td>
                          </tr>
                        ))}

                        {/* Si hay stock directo del sistema */}
                        {stockGanadero
                          .filter(s => {
                            const act = (
                              s.actividad ||
                              s.sistemaProductivo ||
                              ""
                            ).toLowerCase()
                            return !act.includes("tambo")
                          })
                          .map((stock, idx) => {
                            // Solo mostrar si no se procesó arriba
                            const yaIncluido =
                              Object.keys(
                                datosActuales.ganaderia.cria || {},
                              ).includes(stock.categoria) ||
                              Object.keys(
                                datosActuales.ganaderia.invernada || {},
                              ).includes(stock.categoria)

                            if (yaIncluido) return null

                            return (
                              <tr key={`stock-${idx}`}>
                                <td>{stock.categoria || stock.descripcion}</td>
                                <td className="text-end">
                                  {formatNumber(
                                    stock.cantidadCabezas || stock.cantidad,
                                  )}
                                </td>
                                <td className="text-end text-success">
                                  {formatMoney(
                                    stock.valuacionTotalUsd ||
                                      stock.cantidadCabezas *
                                        getValorCabeza(stock.categoria),
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                      <tfoot className="table-light">
                        <tr className="fw-bold">
                          <td>TOTAL</td>
                          <td className="text-end">
                            {formatNumber(metricas.totalCabezas)}
                          </td>
                          <td className="text-end text-success">
                            {formatMoney(metricas.valorStock)}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </CardBody>
                </Card>
              </Col>

              {/* Resumen Lateral */}
              <Col lg={4}>
                <Card className="border-0 shadow-sm mb-3 text-muted">
                  <CardBody className="text-center">
                    <small className="text-muted d-block mb-1">
                      Ingreso Ganadero
                    </small>
                    <h3 className="text-success mb-0">
                      {formatMoney(metricas.ingresoGanadero)}
                    </h3>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm text-muted">
                  <div style={STYLES.headerResumen}>
                    <i className="mdi mdi-clipboard-list me-2" />
                    Resumen del Sistema
                  </div>
                  <CardBody>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Total Cabezas:</span>
                      <strong>{formatNumber(metricas.totalCabezas)}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Valor del Stock:</span>
                      <strong className="text-primary">
                        {formatMoney(metricas.valorStock)}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Categorías:</span>
                      <strong>
                        {Object.keys(datosActuales.ganaderia.cria || {})
                          .length +
                          Object.keys(datosActuales.ganaderia.invernada || {})
                            .length}
                      </strong>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB TAMBO */}
        {/* ============================================= */}
        {hayDatosTambo && (
          <TabPane tabId="tambo">
            <Row className="g-4">
              {/* Datos Productivos */}
              <Col lg={8}>
                <Card className="border-0 shadow-sm">
                  <div style={STYLES.headerTambo}>
                    <i className="mdi mdi-water me-2" />
                    Producción Lechera
                  </div>
                  <CardBody>
                    <Row className="g-3 mb-4">
                      <Col md={4}>
                        <div className="p-3 bg-light rounded text-center">
                          <i
                            className="mdi mdi-cow text-primary"
                            style={{ fontSize: "2rem" }}
                          />
                          <h4 className="mb-0 mt-2">
                            {formatNumber(
                              datosActuales.tambo?.vacasOrdene ||
                                datosActuales.tambo?.vacas_en_ordene ||
                                datosTambo?.vacasOrdene ||
                                0,
                            )}
                          </h4>
                          <small className="text-muted">Vacas en Ordeñe</small>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded text-center">
                          <i
                            className="mdi mdi-water text-info"
                            style={{ fontSize: "2rem" }}
                          />
                          <h4 className="mb-0 mt-2">
                            {formatNumber(
                              datosActuales.tambo?.produccionDiaria ||
                                datosTambo?.produccionDiaria ||
                                0,
                            )}
                          </h4>
                          <small className="text-muted">Litros/Día</small>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 bg-light rounded text-center">
                          <i
                            className="mdi mdi-calendar-month text-success"
                            style={{ fontSize: "2rem" }}
                          />
                          <h4 className="mb-0 mt-2">
                            {formatNumber(
                              datosActuales.tambo?.produccionMensual ||
                                datosTambo?.produccionMensual ||
                                0,
                            )}
                          </h4>
                          <small className="text-muted">Litros/Mes</small>
                        </div>
                      </Col>
                    </Row>

                    {/* Datos adicionales del Excel */}
                    {datosActuales.tambo &&
                      Object.keys(datosActuales.tambo).length > 0 && (
                        <>
                          <h6 className="text-muted mb-3">Datos del Tambo</h6>
                          <Table responsive size="sm" className="mb-0">
                            <tbody>
                              {datosActuales.tambo.litrosPorDia && (
                                <tr>
                                  <td className="text-muted">
                                    Litros por vaca/día
                                  </td>
                                  <td className="text-end fw-bold">
                                    {datosActuales.tambo.litrosPorDia}
                                  </td>
                                </tr>
                              )}
                              {datosActuales.tambo._tambero && (
                                <tr>
                                  <td className="text-muted">% Tambero</td>
                                  <td className="text-end fw-bold">
                                    {datosActuales.tambo._tambero}%
                                  </td>
                                </tr>
                              )}
                              {datosActuales.tambo.precio_vaqreposicion && (
                                <tr>
                                  <td className="text-muted">
                                    Precio vaq. reposición
                                  </td>
                                  <td className="text-end fw-bold">
                                    USD{" "}
                                    {datosActuales.tambo.precio_vaqreposicion}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}

                    {/* Detalle de Stock Tambo */}
                    {(datosTambo?.stock?.length > 0 ||
                      Object.keys(datosActuales.ganaderia.tambo || {}).length >
                        0) && (
                      <>
                        <h6 className="text-muted mb-3 mt-4">Stock de Tambo</h6>
                        <Table responsive size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Categoría</th>
                              <th className="text-end">Cabezas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(
                              datosActuales.ganaderia.tambo || {},
                            ).map(([cat, cant]) => (
                              <tr key={cat}>
                                <td>{cat}</td>
                                <td className="text-end">
                                  {formatNumber(cant)}
                                </td>
                              </tr>
                            ))}
                            {(datosTambo?.stock || []).map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.categoria}</td>
                                <td className="text-end">
                                  {formatNumber(item.cantidadCabezas)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>

              {/* Resumen Económico Tambo */}
              <Col lg={4}>
                <Card className="border-0 shadow-sm mb-3">
                  <CardBody className="text-center">
                    <small className="text-muted d-block mb-1">
                      Ingreso Anual Tambo
                    </small>
                    <h3 className="text-success mb-0">
                      {formatMoney(metricas.ingresoTambo)}
                    </h3>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm mb-3">
                  <CardBody className="text-center">
                    <small className="text-muted d-block mb-1">
                      Costos Anuales
                    </small>
                    <h3 className="text-danger mb-0">
                      {formatMoney(metricas.costosTambo)}
                    </h3>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardBody className="text-center">
                    <small className="text-muted d-block mb-1">
                      Margen Tambo
                    </small>
                    <h3
                      className={
                        metricas.margenTambo >= 0
                          ? "text-success mb-0"
                          : "text-danger mb-0"
                      }
                    >
                      {formatMoney(metricas.margenTambo)}
                    </h3>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm mt-3">
                  <CardBody className="text-muted">
                    {/* BDSJEA-167: los tres últimos parámetros que había acá
                        —costo de alimentación, sanidad y mano de obra— no
                        existen en VALORES_TAMBO ni en agro.valor_general. Se
                        mostraban como "-/lt" de forma permanente: campos que
                        prometían un desglose que nunca iba a llegar. Se
                        reemplazan por los parámetros que el cálculo usa de
                        verdad. */}
                    <h6 className="text-muted mb-3">Parámetros Usados</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">Precio del litro:</small>
                      <small>{formatMoney(VALORES_TAMBO.precioLitro)}/lt</small>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">% tambero:</small>
                      <small>
                        {(VALORES_TAMBO.pctTambero * 100).toFixed(0)}%
                      </small>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">
                        Gasto anual por vaca:
                      </small>
                      <small>{formatMoney(VALORES_TAMBO.gastoAnualVaca)}</small>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">% reposición anual:</small>
                      <small>
                        {(VALORES_TAMBO.pctReposicion * 100).toFixed(0)}%
                      </small>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <small className="text-muted">
                        Vaquillona de reposición:
                      </small>
                      <small>
                        {formatMoney(VALORES_TAMBO.precioVaquillona)}
                      </small>
                    </div>
                    <div className="d-flex justify-content-between">
                      <small className="text-muted">Costo de compra:</small>
                      <small>{formatMoney(VALORES_TAMBO.costoCompra)}</small>
                    </div>
                    <small className="text-muted d-block mt-2">
                      Se administran en Administración &gt; Valores Generales.
                      El cálculo lo resuelve el servidor; esta pestaña muestra
                      el mismo resultado que la pestaña Tambo y que la
                      proyección.
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>
        )}

        {/* ============================================= */}
        {/* TAB ANÁLISIS SATELITAL */}
        {/* ============================================= */}
        <TabPane tabId="satelital">
          <Card className="border-0 shadow-sm mb-4">
            <div style={STYLES.headerSatelital}>
              <i className="mdi mdi-satellite-variant me-2" />
              Análisis Satelital con Inteligencia Artificial
            </div>
            <CardBody>
              <Alert color="info" className="mb-4" timeout={150}>
                <i className="mdi mdi-information me-2" />
                Esta herramienta permite comparar imágenes satelitales de los
                lotes del productor para analizar la evolución de los cultivos y
                generar un diagnóstico automático.
              </Alert>

              <AnalisisSatelitalIA
                idProductor={idProductor}
                onAnalisisGenerado={resultado => {
                  setAnalisisSatelital(resultado)
                  // console.log("Análisis satelital generado:", resultado)
                }}
              />
            </CardBody>
          </Card>

          {/* Mostrar resumen del análisis si existe */}
          {analisisSatelital && (
            <Card className="border-0 shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">
                    <i className="mdi mdi-check-circle text-success me-2" />
                    Análisis Guardado para el Informe
                  </h6>
                  <Button
                    color="outline-danger"
                    size="sm"
                    onClick={() => setAnalisisSatelital(null)}
                  >
                    <i className="mdi mdi-delete me-1" />
                    Eliminar
                  </Button>
                </div>
                <p className="text-muted mb-0">{analisisSatelital.resumen}</p>
                <small className="text-muted">
                  Este análisis se incluirá en el PDF del informe.
                </small>
              </CardBody>
            </Card>
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB PROYECCIÓN CREDITICIA */}
        {/* ============================================= */}
        <TabPane tabId="proyeccion">
          {loadingInforme ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
              <p className="text-muted mt-2">Cargando proyección...</p>
            </div>
          ) : informeCompleto?.proyeccion ? (
            <>
              <SeccionDictamen proyeccion={informeCompleto.proyeccion} />

              <SeccionFlujoCaja
                flujoMensual={informeCompleto.proyeccion?.flujoMensual}
              />

              <SeccionComparativo
                comparativoZona={informeCompleto.comparativoZona}
              />

              <SeccionHistorial
                historialCampanas={informeCompleto.historialCampanas}
              />
            </>
          ) : (
            <Alert color="warning" timeout={150}>
              <i className="mdi mdi-alert me-2" />
              No se pudo generar la proyección crediticia para este productor.
              {informeCompleto?.errorProyeccion && (
                <small className="d-block mt-1 text-muted">
                  {informeCompleto.errorProyeccion}
                </small>
              )}
            </Alert>
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB SCORING + ALERTAS */}
        {/* ============================================= */}
        <TabPane tabId="scoring">
          {loadingInforme ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
            </div>
          ) : informeCompleto?.scoring ? (
            <SeccionScoring
              scoring={informeCompleto.scoring}
              alertas={informeCompleto.alertas}
            />
          ) : (
            <Alert color="info" timeout={150}>
              <i className="mdi mdi-information me-2" />
              No hay scoring calculado para este productor. Puede ejecutar el
              cálculo desde la pestaña de Scoring del productor.
            </Alert>
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB TENENCIA DE TIERRA */}
        {/* ============================================= */}
        <TabPane tabId="tenencia">
          {loadingInforme ? (
            <div className="text-center py-4">
              <Spinner color="primary" size="sm" />
            </div>
          ) : (
            <SeccionTenencia
              tenencia={informeCompleto?.tenencia}
              resumenTenencia={informeCompleto?.resumenTenencia}
            />
          )}
        </TabPane>

        {/* ============================================= */}
        {/* TAB DATOS / ORIGEN */}
        {/* ============================================= */}
        <TabPane tabId="datos">
          <Row className="g-4">
            {/* Datos del Cliente */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm">
                <div style={STYLES.headerCliente}>
                  <i className="mdi mdi-account me-2" />
                  Cliente
                </div>
                <CardBody className="text-muted">
                  <div className="mb-3">
                    <small className="text-muted d-block">Razón Social</small>
                    <strong style={{ fontSize: "1.1rem" }}>
                      {datosActuales.cliente.razonSocial}
                    </strong>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">CUIT</small>
                    <strong>{datosActuales.cliente.cuit}</strong>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Localidad</small>
                    <span>{datosActuales.cliente.localidad}</span>
                  </div>
                  <div>
                    <small className="text-muted d-block">Provincia</small>
                    <span>{datosActuales.cliente.provincia}</span>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Origen de Datos */}
            <Col lg={6}>
              <Card className="border-0 shadow-sm">
                <div style={STYLES.headerDatos}>
                  <i className="mdi mdi-database me-2" />
                  Origen de Datos
                </div>
                <CardBody>
                  <Alert
                    color={origenDatos === "sistema" ? "success" : "info"}
                    className="mb-3"
                  >
                    <div className="d-flex align-items-center">
                      <i
                        className={`mdi ${origenDatos === "sistema" ? "mdi-database-check" : "mdi-microsoft-excel"} me-2`}
                        style={{ fontSize: "1.5rem" }}
                      />
                      <div>
                        <strong className="d-block">
                          {origenDatos === "sistema"
                            ? "Sistema EWAN"
                            : "Excel Importado"}
                        </strong>
                        <small>
                          {origenDatos === "sistema"
                            ? "Datos obtenidos de las actividades registradas"
                            : "Datos cargados desde archivo Excel"}
                        </small>
                      </div>
                    </div>
                  </Alert>

                  <div className="text-muted bg-light rounded p-3">
                    <h6 className="text-muted mb-3">Datos Disponibles</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Siembras cargadas:</span>
                      <Badge
                        color={
                          datosActuales.cultivos.length > 0
                            ? "success"
                            : "secondary"
                        }
                      >
                        {datosActuales.cultivos.length}
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Stock ganadero:</span>
                      <Badge
                        color={
                          metricas.totalCabezas > 0 ? "success" : "secondary"
                        }
                      >
                        {formatNumber(metricas.totalCabezas)} cabezas
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Establecimientos:</span>
                      <Badge
                        color={
                          establecimientos.length > 0 ? "success" : "secondary"
                        }
                      >
                        {establecimientos.length}
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Actividad Tambo:</span>
                      <Badge color={hayDatosTambo ? "success" : "secondary"}>
                        {hayDatosTambo ? "Sí" : "No"}
                      </Badge>
                    </div>
                  </div>

                  {origenDatos === "excel" && (
                    <Button
                      color="outline-success"
                      size="sm"
                      className="mt-3 w-100"
                      onClick={volverADatosSistema}
                    >
                      <i className="mdi mdi-database me-1" />
                      Volver a datos del sistema
                    </Button>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Timestamp */}
          <div className="text-center text-muted mt-4">
            <small>
              <i className="mdi mdi-clock-outline me-1" />
              Generado el {dayjs().format("DD/MM/YYYY HH:mm")}
            </small>
          </div>
        </TabPane>
      </TabContent>

      {/* ============================================= */}
      {/* MODAL CARGA EXCEL */}
      {/* ============================================= */}
      <Modal isOpen={modalExcel} toggle={() => setModalExcel(false)} size="md">
        <ModalHeader toggle={() => setModalExcel(false)}>
          <i className="mdi mdi-microsoft-excel text-success me-2" />
          Cargar Datos desde Excel
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="mb-3" timeout={150}>
            <small>
              <strong>Formato esperado:</strong> El archivo debe contener las
              hojas Caratula, Cultivo, Ganaderia y opcionalmente Tambo.
            </small>
          </Alert>

          <div className="mb-3">
            <label className="form-label">Seleccionar archivo</label>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={e => setArchivoExcel(e.target.files[0])}
            />
          </div>

          {archivoExcel && (
            <Alert color="light" timeout={150}>
              <i className="mdi mdi-file-excel me-2" />
              {archivoExcel.name}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalExcel(false)}>
            Cancelar
          </Button>
          <Button
            color="success"
            onClick={procesarExcel}
            disabled={!archivoExcel || procesandoExcel}
          >
            {procesandoExcel ? (
              <>
                <Spinner size="sm" className="me-1" />
                Procesando...
              </>
            ) : (
              <>
                <i className="mdi mdi-upload me-1" />
                Cargar
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default InformeAgropecuario
